import React, { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { changeLanguage, multilanguage } from "redux-multilanguage";
import { useIsHomePage } from "../../utils/location";
import { deriveAppBase, setCurrentAppBase } from "../../utils/appBase";
import { fetchSite } from "../../redux/actions/siteActions";
import { fetchRootPage } from "../../redux/actions/rootPageActions";
import { fetchBrand } from "../../redux/actions/brandActions";
import { applyBrandTheme } from "../../utils/brandTheme";
import { fetchDynamicPages } from "../../redux/actions/dynamicPagesActions";
import { LANGUAGE_LABELS, doesContainLanguageCode, getAvailableLanguages, getDefaultLanguage, getDirection } from "../../utils/languages";
import Header from "../../components/magnolia/header/Header";
import Footer from "../../components/magnolia/footer/Footer";
import MarketingTags from "../../components/magnolia/marketingTags/MarketingTags";

// For all pages (Magnolia and Static) but Homepage, Footer area is rendered here and takes the content from the store
// Footer area is also conditionally rendered for Homepage in Standard page template to make it editable

// This is also where the CURRENT SITE is established. The app serves multiple storefronts
// (every /shops/<name> tree plus the legacy APP_BASE), and everything site-scoped — root
// page (navigation, footer, brand), site info (locales), dynamic product page paths — hangs
// off the base derived from the URL. The fetches used to run once at store creation against
// the fixed APP_BASE; running them here, re-triggered when the derived base changes, is what
// makes a shop created in the Pages app work without any frontend change.
const BaseLayout = ({ children, currentLanguageCode }) => {
    const rootPage = useSelector((state) => state.rootPageData);
    const brand = useSelector((state) => state.brandData);
    const siteData = useSelector((state) => state.siteData);
    const isHomePage = useIsHomePage();
    const location = useLocation();
    const dispatch = useDispatch();

    // The URL decides the language: a /<lang>/ prefix, else the site's default. The redux
    // language is persisted in localStorage, so without this a visitor who once switched to
    // German kept German UI strings on English URLs. The prefix is matched against every known
    // code, not the loaded site's locales: the site itself is derived with the current language
    // stripped, so judging the prefix by it would flip between two sites.
    useEffect(() => {
        const knownLanguages = [...new Set([...Object.keys(LANGUAGE_LABELS), ...getAvailableLanguages(siteData)])];
        const prefixLanguage = knownLanguages.find((lang) => doesContainLanguageCode(location.pathname, lang));
        if (!prefixLanguage && !siteData?.i18n) return;
        const urlLanguage = prefixLanguage || getDefaultLanguage(siteData);
        if (urlLanguage !== currentLanguageCode) dispatch(changeLanguage(urlLanguage));
    }, [location.pathname, siteData, currentLanguageCode, dispatch]);

    // Brand colors/fonts as CSS variables — see utils/brandTheme.js.
    useEffect(() => {
        applyBrandTheme(brand);
    }, [brand]);

    // Reflect the language on the document; dir switches Arabic to right-to-left.
    useEffect(() => {
        document.documentElement.lang = (currentLanguageCode || "en").replace("_", "-");
        document.documentElement.dir = getDirection(currentLanguageCode);
    }, [currentLanguageCode]);

    const appBase = deriveAppBase(location.pathname, currentLanguageCode);

    useEffect(() => {
        setCurrentAppBase(appBase);
        dispatch(fetchSite(appBase));
        // fetchBrand reads the root page's brand property from the store, so it must run
        // after the root page arrived, not in parallel.
        dispatch(fetchRootPage(appBase)).then(() => {
            dispatch(fetchDynamicPages(appBase));
            dispatch(fetchBrand());
        });
    }, [appBase, dispatch]);

    return (
        <Fragment>
            <MarketingTags appBase={appBase} />
            {rootPage.navigation && <Header navigationId={rootPage.navigation} />}
            {children}
            {!isHomePage && rootPage.footer && <Footer content={rootPage.footer} />}
        </Fragment>
    );
};

export default multilanguage(BaseLayout);
